import type {WizardData} from '../index'
import {classIdentities, classIdentity, levelIdentity} from './academicStructure'
import {
  type CurriculumFields,
  buildLevelCurriculumLookup,
  curriculumFieldsFor,
  lookupLevelId,
  parseLevelIdKey,
} from './levelCurriculum'

type ManualData = WizardData['manualData']
type Loose = Record<string, any>

const trim = (value: unknown): string => String(value ?? '').trim()
const lower = (value: unknown) => trim(value).toLowerCase()

/**
 * Deletes made on Academic Structure are a pending command queue (`deletedClasses`,
 * `deletedLevels`): they only reach the backend on that step's Next. Two things follow.
 *
 * 1. A logout before Next must not lose them, so they are persisted with the classes draft and
 *    restored on resume. But GET /onboarding/data still returns everything they name, and the
 *    resume mapper would paint it straight back — the level with no subjects, the class the
 *    user removed. `applyPendingDeletes` re-applies the queue to the hydrated data so the page
 *    matches what the user last saw, and keeps the queue for the next sync.
 * 2. The bulk-import branch payload only lists the levels that still exist, which leaves a
 *    removed level in the tenant DB. `buildLevelDeletePayload` produces the branch entries that
 *    delete it, sent as a SEPARATE request after the classes request: classes resolve
 *    server-side by name + level, so the level must outlive its classes' delete.
 */

/** Pending deletes of a manualData / classes draft, normalised to arrays. */
export const pendingDeletesOf = (source: Loose | null | undefined) => ({
  deletedClasses: (Array.isArray(source?.deletedClasses)
    ? source!.deletedClasses
    : []) as NonNullable<ManualData['deletedClasses']>,
  deletedLevels: (Array.isArray(source?.deletedLevels) ? source!.deletedLevels : []) as NonNullable<
    ManualData['deletedLevels']
  >,
})

/**
 * Applies `queue` (the saved draft's pending deletes) onto freshly hydrated manualData and
 * returns the result with both queues carried on it. No-op when the queue is empty.
 */
export const applyPendingDeletes = (
  manualData: ManualData,
  queue: Loose | null | undefined
): ManualData => {
  const {deletedClasses, deletedLevels} = pendingDeletesOf(queue)
  if (deletedClasses.length === 0 && deletedLevels.length === 0) return manualData

  const md = manualData as Loose
  const removedLevels = new Set(deletedLevels.map((l) => levelIdentity(l.curriculum, l.name)))
  const removedLevelNames = new Set(deletedLevels.map((l) => lower(l.name)))
  const levelIsRemoved = (curriculum: unknown, level: unknown) =>
    removedLevels.has(levelIdentity(curriculum, level)) ||
    // Rows may carry no curriculum (legacy) — fall back to the bare level name.
    (!trim(curriculum) && removedLevelNames.has(lower(level)))

  // A queued delete names its curriculum (two curricula can share a class name); one queued by
  // an older build has none and matches the class whatever its curriculum.
  const removedClasses = new Set(
    deletedClasses.map((c) => classIdentity(c.name, c.branch, c.level, c.curriculum))
  )
  const isRemovedIdentity = (name: unknown, branch: unknown, level: unknown, curriculum: unknown) =>
    classIdentities(name, branch, level, curriculum).some((identity) =>
      removedClasses.has(identity)
    )
  const classIsRemoved = (row: Loose) =>
    isRemovedIdentity(row?.name, row?.branch, row?.level, row?.curriculum) ||
    levelIsRemoved(row?.curriculum, row?.level)

  // Classes under a removed level go into the class queue too: the classes request must empty
  // the level before the level request can drop it.
  const queuedClasses = [...deletedClasses]
  const queuedIdentities = new Set(removedClasses)
  const classes = ((md.classes ?? []) as Loose[]).filter((row) => {
    if (!classIsRemoved(row)) return true
    const identity = classIdentity(row?.name, row?.branch, row?.level, row?.curriculum)
    const persisted = row?.persisted === true || Number(row?.id) > 0
    if (persisted && !queuedIdentities.has(identity)) {
      queuedIdentities.add(identity)
      queuedClasses.push({
        grade: trim(row?.grade) || trim(row?.name).split('-')[0],
        section: trim(row?.section),
        name: trim(row?.name),
        branch: trim(row?.branch) || undefined,
        level: trim(row?.level),
        curriculum: trim(row?.curriculum) || undefined,
        ...(Number(row?.id) > 0 ? {id: Number(row.id)} : {}),
        ...(row?.persisted === true ? {persisted: true} : {}),
      })
    }
    return false
  })

  const subjects = ((md.subjects ?? []) as Loose[]).filter(
    (row) =>
      !levelIsRemoved(row?.curriculum, row?.level) &&
      !isRemovedIdentity(row?.class, row?.branch, row?.level, row?.curriculum)
  )

  // Review rows: drop a removed level's rows; strip removed sections from the rest.
  const reviewRowsByBranch: Record<string, Loose[]> = {}
  Object.entries((md.classesReviewRowsByBranch ?? {}) as Record<string, Loose[]>).forEach(
    ([branch, rows]) => {
      reviewRowsByBranch[branch] = (rows ?? [])
        .filter((row) => !levelIsRemoved(row?.curriculum, row?.level))
        .map((row) => {
          const sections = ((row?.sections ?? []) as unknown[]).filter(
            (section) =>
              !isRemovedIdentity(
                `${trim(row?.gradeName)}-${trim(section)}`,
                branch,
                row?.level,
                row?.curriculum
              )
          )
          return {...row, sections}
        })
        .filter((row) => row.sections.length > 0)
    }
  )

  // Curriculum config: drop removed levels everywhere; drop grades left with no section.
  const levelsByCurriculum: Record<string, Loose> = {}
  Object.entries((md.levelsByCurriculum ?? {}) as Record<string, Loose>).forEach(
    ([curriculum, cfg]) => {
      const levels = ((cfg?.levels ?? []) as Loose[]).filter(
        (level) => !levelIsRemoved(curriculum, level?.name)
      )
      const levelsPerBranch: Record<string, string[]> = {}
      Object.entries((cfg?.levelsPerBranch ?? {}) as Record<string, unknown[]>).forEach(
        ([branch, names]) => {
          levelsPerBranch[branch] = (names ?? [])
            .map(String)
            .filter((name) => !levelIsRemoved(curriculum, name))
        }
      )
      const levelDetails: Record<string, Loose> = {}
      Object.entries((cfg?.levelDetails ?? {}) as Record<string, Loose>).forEach(
        ([levelName, detail]) => {
          if (levelIsRemoved(curriculum, levelName)) return
          const gradesKept = ((detail?.classes ?? []) as Loose[]).filter((grade) =>
            Object.entries(reviewRowsByBranch).some(([, rows]) =>
              rows.some(
                (row) =>
                  lower(row?.curriculum) === lower(curriculum) &&
                  lower(row?.level) === lower(levelName) &&
                  lower(row?.gradeName) === lower(grade?.name)
              )
            )
          )
          // A level with no review rows at all (e.g. classes never synced) keeps its grades.
          const hasRows = Object.values(reviewRowsByBranch).some((rows) =>
            rows.some(
              (row) =>
                lower(row?.curriculum) === lower(curriculum) &&
                lower(row?.level) === lower(levelName)
            )
          )
          levelDetails[levelName] = {
            ...detail,
            classes: hasRows ? gradesKept : (detail?.classes ?? []),
          }
        }
      )
      levelsByCurriculum[curriculum] = {...cfg, levels, levelsPerBranch, levelDetails}
    }
  )

  const subjectSelectionsByLevel: Record<string, string[]> = {}
  Object.entries((md.subjectSelectionsByLevel ?? {}) as Record<string, string[]>).forEach(
    ([key, value]) => {
      const [curriculum, level] = key.split('|||')
      if (!levelIsRemoved(curriculum, level)) subjectSelectionsByLevel[key] = value
    }
  )

  const levelsCount = Object.values(levelsByCurriculum).reduce(
    (sum, cfg) => sum + ((cfg?.levels ?? []) as unknown[]).length,
    0
  )

  // Spread the TYPED manualData (not the loose alias) so the required fields survive the
  // literal; `classesReviewRowsByBranch` is not on the ManualData type (the wizard reads it
  // via `as any` everywhere) and rides along as an extra key.
  const result: ManualData & Loose = {
    ...manualData,
    classes: classes as ManualData['classes'],
    classesCount: classes.length,
    subjects: subjects as ManualData['subjects'],
    subjectsCount: subjects.length,
    classesReviewRowsByBranch: reviewRowsByBranch,
    levelsByCurriculum: levelsByCurriculum as ManualData['levelsByCurriculum'],
    levelsCount,
    subjectSelectionsByLevel,
    deletedClasses: queuedClasses,
    deletedLevels,
  }
  return result
}

export type LevelDeleteBranchPayload = {
  name: string
  id?: number
  action: 'update'
  levels: Array<{name: string; id: number; action: 'delete'} & CurriculumFields>
}

/**
 * Whether another curriculum still has a level called `name` in `branch` — then a bare-name id
 * in `branchLevelIdMap` may be that level's, and must not be used to delete this one.
 */
const sharedWithOtherCurriculum = (
  lookup: ReturnType<typeof buildLevelCurriculumLookup>,
  branch: string,
  name: string,
  curriculum: unknown
) => lookup.curriculaFor(branch, name).some((other) => lower(other) !== lower(curriculum))

/**
 * Branch entries deleting the queued levels, for the follow-up bulk-import request. A level is
 * addressed by the server id `branchLevelIdMap` holds for it on each branch (by curriculum +
 * name, or by bare name while no other curriculum shares it); a level with no id anywhere was
 * never synced and is simply dropped from the queue. Returns [] when nothing is deletable.
 * Every branch entry is `update` — the backend never reads the levels of a branch marked
 * delete. Each level also carries its curriculum: the backend refuses a delete by a name that
 * exists more than once in the branch without it.
 */
export const buildLevelDeletePayload = (manualData: ManualData): LevelDeleteBranchPayload[] => {
  const {deletedLevels} = pendingDeletesOf(manualData)
  if (deletedLevels.length === 0) return []
  const levelIdMap = ((manualData as Loose).branchLevelIdMap ?? {}) as Record<
    string,
    Record<string, number>
  >
  const branchIdByName = (manualData.branchIdByName ?? {}) as Record<string, number>
  const lookup = buildLevelCurriculumLookup(manualData)
  const byBranch = new Map<string, LevelDeleteBranchPayload>()
  deletedLevels.forEach((level) => {
    Object.entries(levelIdMap).forEach(([branchName, levels]) => {
      const id = lookupLevelId(
        levels,
        level.name,
        level.curriculum,
        sharedWithOtherCurriculum(lookup, branchName, level.name, level.curriculum)
      )
      if (!id) return
      const entry =
        byBranch.get(branchName) ??
        ({
          name: branchName,
          ...(Number(branchIdByName[branchName]) > 0
            ? {id: Number(branchIdByName[branchName])}
            : {}),
          action: 'update',
          levels: [],
        } as LevelDeleteBranchPayload)
      if (!entry.levels.some((l) => l.id === id)) {
        entry.levels.push({
          name: trim(level.name),
          id,
          ...curriculumFieldsFor(level.curriculum, manualData),
          action: 'delete',
        })
      }
      byBranch.set(branchName, entry)
    })
  })
  return Array.from(byBranch.values())
}

/**
 * manualData after the level-delete request succeeded: queue consumed, ids forgotten. A
 * curriculum-qualified entry goes with its level; a bare-name entry only when it held the
 * deleted level's id (with two curricula it may be the surviving level's).
 */
export const consumeDeletedLevels = (manualData: ManualData): ManualData => {
  const {deletedLevels} = pendingDeletesOf(manualData)
  const levelIdMap = ((manualData as Loose).branchLevelIdMap ?? {}) as Record<
    string,
    Record<string, number>
  >
  const lookup = buildLevelCurriculumLookup(manualData)
  const nextMap: Record<string, Record<string, number>> = {}
  Object.entries(levelIdMap).forEach(([branch, levels]) => {
    const deletedIds = new Set(
      deletedLevels
        .map((level) =>
          lookupLevelId(
            levels,
            level.name,
            level.curriculum,
            sharedWithOtherCurriculum(lookup, branch, level.name, level.curriculum)
          )
        )
        .filter((id): id is number => id !== null)
    )
    const isRemoved = (key: string, id: number) => {
      const parsed = parseLevelIdKey(key)
      return deletedLevels.some((level) => {
        if (lower(level.name) !== lower(parsed.name)) return false
        if (parsed.curriculum) return lower(parsed.curriculum) === lower(level.curriculum)
        return deletedIds.has(Number(id)) || !trim(level.curriculum)
      })
    }
    nextMap[branch] = Object.fromEntries(
      Object.entries(levels ?? {}).filter(([key, id]) => !isRemoved(key, id))
    )
  })
  // `branchLevelIdMap` is not on the ManualData type either (read via `as any` by the payload
  // builder and bulkImportIdMerge); same typed spread with an extra key.
  const result: ManualData & Loose = {...manualData, deletedLevels: [], branchLevelIdMap: nextMap}
  return result
}
